/*
 * rheincool — Produktgalerie
 * - Thumbnails wechseln das Hauptbild (Tabs-Pattern)
 * - Mobil: Swipe via Scroll-Snap, Dots zeigen Position
 * - Zoom-on-Hover auf dem aktiven Bild (Desktop, nur mit Feinzeiger)
 * - Grossansicht (Lightbox) per Klick, Tipp oder Tastatur
 */
class RcGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dotsContainer = this.querySelector('[data-gallery-dots]');

    this.buildDots();
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(thumb.dataset.mediaId));
    });
    if (this.track) {
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    }
    this.initZoom();
    this.initLightbox();
  }

  /*
    Grossansicht.

    Vorher passierte beim Klick auf das Bild gar nichts — es gab nur den
    Hover-Zoom, und den auch nur auf Geraeten mit Feinzeiger. Auf dem Telefon
    war das Bild damit nicht vergroesserbar, per Tastatur ueberhaupt nicht
    erreichbar.

    Das <dialog> uebernimmt Fokusfalle, Escape und das Stilllegen des
    Hintergrunds. Ergaenzt werden hier nur Navigation, Zaehler und der
    Klick auf den Hintergrund.
  */
  initLightbox() {
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;

    this.lbImage = this.lightbox.querySelector('[data-lightbox-image]');
    this.lbCounter = this.lightbox.querySelector('[data-lightbox-counter]');

    // Nur Bild-Slides sind vergroesserbar — Video und 3D-Modell nicht.
    this.zoomSlides = this.slides.filter((slide) => slide.querySelector('[data-full]'));
    if (this.zoomSlides.length === 0) return;

    const oeffnen = () => this.openLightbox();

    const knopf = this.querySelector('[data-gallery-expand]');
    if (knopf) knopf.addEventListener('click', oeffnen);

    // Klick direkt aufs Bild: der erwartete Weg fuer Maus und Finger.
    this.querySelectorAll('[data-full]').forEach((img) => {
      img.addEventListener('click', oeffnen);
    });

    this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
    this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.stepLightbox(-1));
    this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.stepLightbox(1));

    // Klick auf den Hintergrund schliesst. Das Ziel ist dann das <dialog>
    // selbst; Klicks auf Bild oder Knoepfe haben ein Kind als Ziel.
    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.lightbox.close();
    });

    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.stepLightbox(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.stepLightbox(-1); }
    });

    /*
      Aufraeumen beim Schliessen — bewusst ueber das `open`-Attribut und nicht
      ueber das `close`-Event: Escape schliesst das <dialog> nativ, ohne durch
      unseren Code zu laufen, und das `close`-Event kam beim Test nicht
      zuverlaessig an. Der Beobachter greift dagegen bei jedem Weg — Escape,
      Klick auf den Hintergrund, Schliessen-Knopf und programmatisch.
    */
    this.lbBeobachter = new MutationObserver(() => {
      if (this.lightbox.open) return;
      document.body.classList.remove('rc-no-scroll');
      const ziel = this.lbOpener && document.contains(this.lbOpener)
        ? this.lbOpener
        : this.querySelector('[data-gallery-expand]');
      if (ziel && typeof ziel.focus === 'function') ziel.focus();
    });
    this.lbBeobachter.observe(this.lightbox, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    if (this.lbBeobachter) this.lbBeobachter.disconnect();
  }

  openLightbox() {
    const aktiv = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    const start = this.zoomSlides.indexOf(this.slides[aktiv]);
    this.lbIndex = start >= 0 ? start : 0;
    // Merken, wohin der Fokus zurueckgehoert. Wurde per Maus aufs Bild
    // geklickt, steht `activeElement` auf <body> — dann ist die Schaltflaeche
    // das sinnvollere Ziel.
    const fokussiert = document.activeElement;
    this.lbOpener = fokussiert && fokussiert !== document.body && this.contains(fokussiert)
      ? fokussiert
      : this.querySelector('[data-gallery-expand]');
    this.showLightbox();
    document.body.classList.add('rc-no-scroll');
    this.lightbox.showModal();
  }

  stepLightbox(richtung) {
    const anzahl = this.zoomSlides.length;
    this.lbIndex = (this.lbIndex + richtung + anzahl) % anzahl;
    this.showLightbox();
    // Die Galerie dahinter mitfuehren, damit nach dem Schliessen dasselbe
    // Bild steht wie in der Grossansicht.
    const mediaId = this.zoomSlides[this.lbIndex].dataset.mediaId;
    if (mediaId) this.goTo(mediaId);
  }

  showLightbox() {
    const quelle = this.zoomSlides[this.lbIndex].querySelector('[data-full]');
    if (!quelle) return;
    this.lbImage.src = quelle.dataset.full;
    this.lbImage.alt = quelle.alt || '';
    if (this.lbCounter) {
      // Der Zaehler wird per aria-live angesagt — „Bild 2 von 4" ist dabei
      // verstaendlicher als „2 / 4". Der Wortlaut kommt aus der Sprachdatei.
      const vorlage = this.lbCounter.dataset.vorlage || '%%I%% / %%N%%';
      this.lbCounter.textContent = vorlage
        .replace('%%I%%', String(this.lbIndex + 1))
        .replace('%%N%%', String(this.zoomSlides.length));
    }
  }

  buildDots() {
    if (!this.dotsContainer || this.slides.length < 2) return;
    this.slides.forEach((slide, index) => {
      const dot = document.createElement('span');
      dot.className = index === 0 ? 'rc-gallery__dot is-active' : 'rc-gallery__dot';
      this.dotsContainer.appendChild(dot);
    });
    this.dots = Array.from(this.dotsContainer.children);
  }

  goTo(mediaId) {
    const index = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
    if (index < 0) return;
    this.setActive(index);
    this.track.scrollTo({
      left: this.slides[index].offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    if (this.scrollTimer) cancelAnimationFrame(this.scrollTimer);
    this.scrollTimer = requestAnimationFrame(() => {
      const index = Math.round(this.track.scrollLeft / this.track.offsetWidth);
      this.setActive(Math.min(index, this.slides.length - 1), true);
    });
  }

  setActive(index, fromScroll = false) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    if (this.dots) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
    if (!fromScroll && this.track && window.matchMedia('(min-width: 990px)').matches) {
      // Desktop: kein horizontales Scrollen — Slides werden gestapelt umgeschaltet
    }
  }

  initZoom() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.querySelectorAll('[data-zoomable]').forEach((img) => {
      const slide = img.closest('[data-gallery-slide]');
      slide.addEventListener('mousemove', (event) => {
        const rect = slide.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      });
      slide.addEventListener('mouseenter', () => img.classList.add('is-zoomed'));
      slide.addEventListener('mouseleave', () => img.classList.remove('is-zoomed'));
    });
  }
}

customElements.define('rc-gallery', RcGallery);
